/**
 * HI-SI Dashboard - Main Application Component
 * Entry point for the React application with routing and layout
 * Updated: Testing Render auto-deployment
 * TEMP-H-001: Added TanStack Query v5 integration
 */

import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { API_BASE_URL } from '@/lib/api-base';
import RequireAuth from '@/components/auth/RequireAuth';
import DashboardLayout from '@/components/layout/DashboardLayout';
import LoadingScreen from '@/components/ui/LoadingScreen';
import AuthCallback from '@/pages/AuthCallback';
import AuthSuccess from '@/pages/AuthSuccess';
import ExecutionPage from '@/pages/ExecutionPage';
import ExecutionPortal from '@/pages/ExecutionPortal';
import ExecutionSettingsPage from '@/pages/ExecutionSettingsPage';
import Dashboard from '@/pages/Dashboard';
import Executions from '@/pages/Executions';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import ProfileEditorPage from '@/pages/ProfileEditorPage';
import ProfilesListPage from '@/pages/ProfilesListPage';
import ProjectsPage from '@/pages/ProjectsPage';
import SettingsPage from '@/pages/SettingsPage';
import SignupPage from '@/pages/SignupPage';
import DemoViewerPage from '@/pages/DemoViewerPage';
import DeploymentsPage from '@/pages/DeploymentsPage';
import { useDashboardServerStore } from '@/stores/dashboard-server';

// Configuration
// H-011: Use unified API base URL
export { API_BASE_URL };

// H-011: Create QueryClient instances for different use cases
const dashboardQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false, // Disable aggressive refetching
    },
    mutations: {
      retry: 1,
    },
  },
});

function App() {
  const { isLoading, setLoading } = useDashboardServerStore();
  const location = useLocation();

  // Use single production query client for all routes including stage-tester
  const queryClient = dashboardQueryClient;

  // HISI-FE-AUTH-01: OAuth code processing moved to AuthCallback.tsx to prevent race conditions
  // The AuthCallback component will handle all OAuth code exchange logic
  // DV-AUTH-005: Authentication state now handled entirely by RequireAuth component

  useEffect(() => {
    // Initialize dashboard server state for all routes
    // Auth-specific logic has been moved to RequireAuth component
    console.log('🎬 Initializing HI-SI Dashboard...');
    console.log('📡 Server API URL:', API_BASE_URL);
    // Realtime is now handled by secure Socket.IO hooks within pages (e.g., ExecutionPortal)
    // Set loading false here; page-level hooks will manage their own loading states
    setLoading(false);

    // Cleanup on unmount or route change
    return () => {
      const store = useDashboardServerStore.getState();
      store.disconnectWebSocket();
    };
  }, [location.pathname, setLoading]);

  // Show loading screen only during initial loading
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <div className="App">
          <Routes>
            {/* HI-SI Landing Page */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/success" element={<AuthSuccess />} />
            <Route element={<RequireAuth />}>
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="executions" element={<Executions />} />
                <Route path="execution/:executionId" element={<ExecutionPage />} />
                <Route path="executions/:executionId/settings" element={<ExecutionSettingsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/profiles" element={<ProfilesListPage />} />
                <Route path="settings/profiles/:profileId" element={<ProfileEditorPage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="deployments" element={<DeploymentsPage />} />
                {/* Portal is inside DashboardLayout to keep left menu */}
                <Route path="portal/:executionId" element={<ExecutionPortal />} />
              </Route>
              {/* Demo Viewer - full-width, outside standard layout */}
              <Route path="/dashboard/demo" element={<DemoViewerPage />} />
            </Route>
            {/* Auth routes */}
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;